import { Link, useNavigate } from 'react-router-dom';
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  EyeIcon,
  EyeSlashIcon,
  EnvelopeIcon,
  LockClosedIcon,
  ArrowRightIcon,
  ShieldCheckIcon,
  StarIcon,
  RocketLaunchIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { resolveAuthError } from '../../utils/apiError';
import AuthPageShell from '../ui/AuthPageShell';
import InputIcon from '../ui/InputIcon';
import Button from '../ui/Button';
import BrandMark from '../ui/BrandMark';
import FormField from '../ui/FormField';
import { validateEmail, validatePassword, validateFields } from '../../utils/formValidation';
import { inputClassWithError } from '../../styles/forms';

function Login() {
  const { t, formatMessage } = useLanguage();
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitError, setSubmitError] = useState('');

  const { login } = useAuth();
  const navigate = useNavigate();
  const validation = t.ux?.validation || {};

  const validateForm = () => {
    const { valid, errors } = validateFields({
      email: validateEmail(formData.email, {
        required: validation.emailRequired || 'Email is required',
        invalid: validation.emailInvalid || 'Enter a valid email address',
      }),
      password: validatePassword(formData.password, {
        required: validation.passwordRequired || 'Password is required',
        min: validation.passwordMin || 'Password must be at least 6 characters',
      }),
    });
    setFieldErrors(errors);
    return valid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    if (!validateForm()) return;

    setLoading(true);

    try {
      const result = await login(formData.email, formData.password);

      if (result.success) {
        toast.success(t.login.welcomeBackToast);
        // Redirect based on user type after successful login
        setTimeout(() => {
          // Check user from result since state might not be updated yet
          const loggedInUser = result.user;
          if (loggedInUser?.is_admin) {
            navigate('/admin');
          } else if (loggedInUser?.is_vendor) {
            navigate('/vendor');
          } else {
            navigate('/dashboard');
          }
        }, 1000);
      } else {
        const errMsg = resolveAuthError(result, t, formatMessage, t.login.signInFailed);
        setSubmitError(errMsg);
        toast.error(errMsg);
      }
    } catch (error) {
      setSubmitError(t.login.errorToast);
      toast.error(t.login.errorToast);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({ ...prev, [name]: null }));
    }
    if (submitError) setSubmitError('');
  };

  const formVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }
    },
    exit: {
      opacity: 0,
      y: 30,
      transition: { duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }
    }
  };

  const features = [
    {
      icon: RocketLaunchIcon,
      title: t.login.features.launch.title,
      description: t.login.features.launch.desc
    },
    {
      icon: ShieldCheckIcon,
      title: t.login.features.secure.title,
      description: t.login.features.secure.desc
    },
    {
      icon: StarIcon,
      title: t.login.features.premium.title,
      description: t.login.features.premium.desc
    }
  ];

  return (
    <AuthPageShell backTo="/" backLabel={t.login.backToHome}>
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="mx-auto grid w-full max-w-6xl grid-cols-1 items-center gap-4 px-3 sm:gap-8 sm:px-4 lg:grid-cols-2 lg:gap-12"
      >
        {/* Left Side - Branding & Features */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="text-center lg:text-left order-1 lg:order-1"
        >
          <div className="mb-4 sm:mb-8">
            <BrandMark variant="hero" className="mb-5 sm:mb-6 lg:mb-8" />

            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="text-lg font-bold leading-snug text-secondary-900 mb-1 px-2 sm:hidden"
            >
              {t.login.welcome} <span className="text-gradient">{t.login.future}</span>
            </motion.h2>

            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="text-lg sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold text-secondary-900 mb-2 sm:mb-4 hidden sm:block"
            >
              {t.login.welcome}
              <span className="text-gradient block mt-1">{t.login.future}</span>
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="text-xs sm:text-base md:text-lg text-secondary-600 leading-relaxed max-w-md mx-auto lg:mx-0 px-2 sm:px-0 hidden sm:block"
            >
              {t.login.subtitle}
            </motion.p>
          </div>

          {/* Features */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="space-y-3 sm:space-y-4 lg:space-y-6 hidden sm:block"
          >
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.6 + index * 0.1, ease: [0.25, 0.46, 0.45, 0.94] }}
                className="feature-row"
              >
                <div className="icon-tile h-10 w-10 sm:h-12 sm:w-12 from-orange-500 to-amber-500">
                  <feature.icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <div className="text-left">
                  <h3 className="font-semibold text-sm sm:text-base text-secondary-900">{feature.title}</h3>
                  <p className="text-xs sm:text-sm text-secondary-600">{feature.description}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>

        {/* Right Side - Login Form */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="w-full max-w-md mx-auto order-2 lg:order-2"
        >
          <div className="auth-card p-4 sm:p-6 md:p-8 lg:p-10">
            {/* Form Header */}
            <div className="text-center mb-4 sm:mb-8">
              <motion.h3
                className="text-lg sm:text-2xl font-bold text-secondary-900 mb-1 sm:mb-2"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
              >
                {t.login.welcomeBack}
              </motion.h3>
              <motion.p
                className="text-xs sm:text-base text-secondary-600"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
              >
                {t.login.signInAccount}
              </motion.p>
            </div>

            {/* Form */}
            <motion.form
              variants={formVariants}
              initial="hidden"
              animate="visible"
              onSubmit={handleSubmit}
              className="space-y-3 sm:space-y-6"
            >
              <FormField
                id="login-email"
                label={t.login.email}
                error={fieldErrors.email}
                className="text-xs sm:text-sm"
                asChild
              >
                <InputIcon icon={EnvelopeIcon}>
                  <input
                    id="login-email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={inputClassWithError(Boolean(fieldErrors.email), 'input-premium py-2.5 text-sm sm:py-3 sm:text-base')}
                    placeholder={t.login.enterEmail}
                    autoComplete="email"
                    aria-invalid={fieldErrors.email ? true : undefined}
                  />
                </InputIcon>
              </FormField>

              <FormField
                id="login-password"
                label={t.login.password}
                error={fieldErrors.password}
                className="text-xs sm:text-sm"
                asChild
              >
                <InputIcon icon={LockClosedIcon}>
                  <input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className={inputClassWithError(Boolean(fieldErrors.password), 'input-premium py-2.5 pr-12 text-sm sm:py-3 sm:text-base')}
                    placeholder={t.login.enterPassword}
                    autoComplete="current-password"
                    aria-invalid={fieldErrors.password ? true : undefined}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 z-10 -translate-y-1/2 rounded-lg p-1 text-secondary-400 transition-colors hover:bg-secondary-100/80 hover:text-secondary-700 sm:right-4"
                    aria-label={showPassword ? t.common.hidePassword : t.common.showPassword}
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                    ) : (
                      <EyeIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                    )}
                  </button>
                </InputIcon>
              </FormField>

              {submitError ? (
                <p role="alert" className="rounded-2xl border border-error-200 bg-error-50 px-3 py-2 text-sm text-error-700   ">
                  {submitError}
                </p>
              ) : null}

              {/* Submit Button */}
              <Button
                type="submit"
                variant="primary"
                loading={loading}
                disabled={loading}
                icon={ArrowRightIcon}
                iconPosition="right"
                className="w-full py-2.5 sm:py-4 mt-4 sm:mt-8"
              >
                {t.login.signIn}
              </Button>

              {/* Additional Options */}
              <div className="text-center pt-1 sm:pt-0">
                <Link
                  to="/forgot-password"
                  className="text-xs sm:text-sm text-orange-600 hover:text-orange-700 font-medium transition-colors"
                >
                  {t.login.forgotPassword}
                </Link>
              </div>
            </motion.form>
            <p className="text-center text-sm text-secondary-500 mt-6">
              {t.login.businessPortalPrompt}{' '}
              <Link to="/enterprise" className="text-orange-600 font-semibold hover:text-orange-700">
                {t.landing.nav.forBusiness}
              </Link>
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AuthPageShell>
  );
}

export default Login;
